import React, { useEffect, useRef, useState } from 'react';

/**
 * Intro cinemática de La Botica.
 * - Reproduce /intro.mp4 fullscreen (muted + playsInline: requisito de autoplay).
 * - La barra de progreso avanza con el tiempo real del video (doble rol: loading).
 * - Siempre saltable. Se muestra una vez por sesión (sessionStorage).
 * - Degradación elegante: si el video no existe, falla o no arranca en 3.5s,
 *   la web entra directo. Con prefers-reduced-motion, no se muestra.
 */

const VIDEO_SRC = '/intro.mp4';
const START_TIMEOUT_MS = 3500;

export default function Intro({ onDone }) {
  const videoRef = useRef(null);
  const doneRef = useRef(false);
  const startedRef = useRef(false);
  const [progress, setProgress] = useState(0);
  const [fading, setFading] = useState(false);
  const [needsUnmute, setNeedsUnmute] = useState(false);
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (sessionStorage.getItem('introSeen')) return false;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return true;
  });

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    try { sessionStorage.setItem('introSeen', '1'); } catch (e) { /* modo privado */ }
    if (!startedRef.current) {
      // el video nunca arrancó (no existe o falló): entrar directo, sin fade
      setVisible(false);
      if (onDone) onDone();
      return;
    }
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      if (onDone) onDone();
    }, 900);
  };

  useEffect(() => {
    if (!visible) {
      if (onDone) onDone();
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    const startTimer = setTimeout(() => {
      if (!startedRef.current) finish();
    }, START_TIMEOUT_MS);

    const onPlaying = () => { startedRef.current = true; };
    const onTime = () => {
      if (video.duration && isFinite(video.duration)) {
        setProgress(Math.min(video.currentTime / video.duration, 1));
      }
    };
    const onEnded = () => finish();
    const onError = () => finish();

    video.addEventListener('playing', onPlaying);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);

    // primero con sonido; si el navegador lo bloquea, muteado + botón
    video.muted = false;
    video.play().catch(() => {
      video.muted = true;
      setNeedsUnmute(true);
      video.play().catch(() => finish());
    });

    return () => {
      clearTimeout(startTimer);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  return (
    <div className={`intro ${fading ? 'intro-fading' : ''}`}>
      <video
        ref={videoRef}
        className="intro-video"
        src={VIDEO_SRC}
        playsInline
        preload="auto"
      />
      <div className="intro-veil" />
      <div className="intro-grid" />
      <div className="intro-ui">
        <div className="intro-logo-glitch">
          <img src="/logo-labotica.svg" alt="Agencia La Botica" className="intro-logo" />
          <img src="/logo-labotica.svg" alt="" aria-hidden="true" className="intro-logo l-ghost l-r" />
          <img src="/logo-labotica.svg" alt="" aria-hidden="true" className="intro-logo l-ghost l-c" />
          <span className="intro-scanline" />
        </div>
        <div className="intro-progress-track">
          <div className="intro-progress-fill" style={{ transform: `scaleX(${progress})` }} />
        </div>
        <p className="intro-loading">Cargando laboratorio</p>
      </div>
      {needsUnmute && (
        <button
          className="intro-sound"
          onClick={() => {
            const v = videoRef.current;
            if (v) v.muted = false;
            setNeedsUnmute(false);
          }}
        >
          ♪ activar sonido
        </button>
      )}
      <button className="intro-skip" onClick={finish}>
        saltar intro →
      </button>
    </div>
  );
}
